import { Controller } from 'stimulus'
import {
  updateAddressInfo,
  updateCartOnAddressChange,
  updateCartOnPostalCodeChange,
} from '~/site/pages/cart/common'
import { VAT_CHECK_PENDING, cartForm } from '~/site/pages/cart/vat_check_lock'

// The lock lives on the cart form as an attribute, not on the buttons.
//
// checkout_update re-renders #cart-payment on every cart update, so anything we disable or
// reveal inside it is thrown away mid-flight and comes back enabled — while the queued VIES
// lookup is still running. The form itself is never replaced. Keeping the state there also
// stays clear of Stripe's own js-disabled-for-stripe bookkeeping: we never touch
// button.disabled, so neither side can release the other's lock.
//
// What the attribute drives: the wait notice and the button's disabled look, both via CSS in
// payment_form_component.scss, and the real guard in
// payment_form_component_controller#handlePaymentFormSubmit, which refuses to submit while it
// is set. That guard is what makes Enter-to-submit safe too.
// Generous, because it only exists so a latch that never clears cannot strand the buyer. One
// lookup can legitimately cost OPEN_TIMEOUT + CHECKOUT_READ_TIMEOUT twice over (Savon fetches
// the WSDL, then makes the SOAP call), and a queued update waits for the request ahead of it.
const CART_IDLE_POLL_MS = 100
const CART_IDLE_TIMEOUT_MS = 180_000

// A live region the buyer's screen reader can actually hear. It has to satisfy two things at
// once: be present and perceivable to assistive tech before the text changes, and survive the
// #cart-payment re-render. The visible notice can do neither — it is inside the re-rendered
// region and starts display:none, and a pre-populated region merely becoming visible is not
// reliably announced. So this one lives on the cart form, which is never replaced, and its
// text is what changes.
const ANNOUNCER_CLASS = 'js-vat-check-announcer'

function announcer() {
  const form = cartForm()
  if (!form) return null

  let element = form.querySelector(`.${ANNOUNCER_CLASS}`)
  if (!element) {
    element = document.createElement('p')
    element.className = `${ANNOUNCER_CLASS} sr-only`
    element.setAttribute('role', 'status')
    element.setAttribute('aria-live', 'polite')
    form.prepend(element)
  }
  return element
}

// Creating a live region and filling it in the same tick is commonly missed: assistive tech
// tends to announce changes to a region it already knows about, not one that arrives
// pre-populated. The region is created empty on connect, and the text lands on a later tick.
function announce(message) {
  const element = announcer()
  if (!element) return

  setTimeout(() => {
    element.textContent = message
  }, 0)
}

function startVatCheck(message) {
  cartForm()?.setAttribute(VAT_CHECK_PENDING, '1')
  announce(message)
}

function endVatCheck() {
  cartForm()?.removeAttribute(VAT_CHECK_PENDING)
  announce('')
}

// doUpdate early-returns with `true` when another cart update is already in flight, queueing
// this one for finishCartUpdate to replay. Awaiting it therefore resolves before our VIES
// lookup has run at all, so releasing the lock then would free Pay while the answer is still
// pending - the exact case the lock exists for. Wait for the form's own latches to clear.
async function cartUpdateSettled() {
  const form = cartForm()
  if (!form) return

  const deadline = Date.now() + CART_IDLE_TIMEOUT_MS
  while (
    (form.hasAttribute('data-loading-cart-update') ||
      form.hasAttribute('data-pending-cart-update')) &&
    Date.now() < deadline
  ) {
    await new Promise((resolve) => setTimeout(resolve, CART_IDLE_POLL_MS))
  }
}

export default class extends Controller {
  static targets = ['showCompanyNameField']
  static values = { vatCheckMessage: String }

  connect() {
    // Empty, but present in the accessibility tree before the first lookup, so the message
    // that lands later is heard as a change rather than as a region appearing fully formed.
    announcer()
  }
  LOADING_CLASSES = ['cursor-wait', 'opacity-50']

  async updateCart() {
    this.element.classList.add(...this.LOADING_CLASSES)
    await updateCartOnAddressChange()
    this.element.classList.remove(...this.LOADING_CLASSES)
  }

  // A VAT number can send this request to VIES, which takes 12-17s for some member states.
  // Hold payment for that window - a buyer who submits first gets charged on a purchase whose
  // VAT never resolved - and say why, so the wait does not read as a hung page.
  //
  // Clearing the field holds too: removing a number that had been reverse-charged puts the VAT
  // back, and submitting before that lands would pay the stale zero-rated total.
  async updateCartForVatNo() {
    this.element.classList.add(...this.LOADING_CLASSES)
    startVatCheck(this.vatCheckMessageValue)
    try {
      await updateCartOnAddressChange()
      await cartUpdateSettled()
    } finally {
      endVatCheck()
      this.element.classList.remove(...this.LOADING_CLASSES)
    }
  }

  async updateCartForPostalCode() {
    this.element.classList.add(...this.LOADING_CLASSES)
    await updateCartOnPostalCodeChange()
    this.element.classList.remove(...this.LOADING_CLASSES)
  }

  showCompanyNameField() {
    this.showCompanyNameFieldTarget.value = '1'
    updateAddressInfo()
  }
}
